/**
 * Lightweight Google Analytics (gtag) helpers.
 * Safe to call before gtag loads — events are no-ops until the script is ready.
 */

declare global {
  interface Window {
    // GA gtag is loaded in root layout via LazyGoogleAnalytics (lazyOnload).
    gtag?: (
      command: "event" | "config" | "js" | "set",
      targetOrAction: string,
      params?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

export type IndustryCtaLocation =
  | "hero"
  | "final_cta"
  | "product_card"
  | "product_modal"
  | "product_detail_open";

type TrackEventParams = Record<string, string | number | boolean | undefined>;

/**
 * Send a GA4 event if gtag is available on the window.
 */
export function trackEvent(eventName: string, params: TrackEventParams = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  // Strip undefined values so GA does not receive empty params.
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }

  window.gtag("event", eventName, cleaned);
}

/**
 * Industry landing page CTA click (quote / pricing buttons).
 */
export function trackIndustryCtaClick(args: {
  vertical: string;
  ctaLocation: IndustryCtaLocation;
  label: string;
}) {
  trackEvent("industry_cta_click", {
    vertical: args.vertical,
    cta_location: args.ctaLocation,
    label: args.label,
  });
}

/**
 * Fired once when an industry landing page mounts (includes UTMs when present).
 */
export function trackIndustryLandingView(args: {
  vertical: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}) {
  trackEvent("industry_landing_view", {
    vertical: args.vertical,
    utm_source: args.utmSource,
    utm_medium: args.utmMedium,
    utm_campaign: args.utmCampaign,
  });
}

/**
 * Contact form successfully submitted.
 */
export function trackContactFormSubmit(args?: {
  source?: string;
  vertical?: string;
}) {
  trackEvent("contact_form_submit", {
    source: args?.source ?? "contact_modal",
    vertical: args?.vertical,
  });
}
